import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Snackbar,
  Alert,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { isMSP } from "../../helpers/util_helper";
import { getInvoiceDownload } from "../../helpers/auth_aws_helper";
import {
  getCustomerInvoices,
  getCustomerBillingMetrics,
  getInvoiceRuleSnapshots,
} from "../../store/billingManagement/billingManagementSlice";
import CustomerInvoicesHeader from "./CustomerInvoicesHeader";
import CustomerInvoicesMetrics from "./CustomerInvoicesMetrics";

// Format date for display (avoid timezone off-by-one)
const formatDate = (dateString) => {
  if (!dateString) return "—";
  try {
    const s = String(dateString);
    if (s.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [y, m, d] = s.split("-").map(Number);
      const date = new Date(y, m - 1, d);
      return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
};

// Short invoice ref for table display (e.g. INV-1069c from invoice_id), same as index.js
const getShortInvoiceRef = (invoiceId) => {
  if (!invoiceId) return null;
  const clean = String(invoiceId).replace(/-/g, "");
  return clean.length >= 5 ? `INV-${clean.slice(-5)}` : `INV-${clean}`;
};

// Sum amount from metrics.by_rule_category for an invoice
const getInvoiceAmount = (inv) => {
  const m = inv.metrics?.by_rule_category;
  if (!m || typeof m !== "object") return null;
  const sum = Object.values(m).reduce((a, b) => a + (Number(b) || 0), 0);
  return sum;
};

// Format currency (same as index.js: full precision for smaller amounts, compact for large)
const formatCurrency = (value) => {
  if (value == null || (typeof value !== "number" && typeof value !== "string")) return "—";
  const num = typeof value === "string" ? parseFloat(value, 10) : value;
  if (Number.isNaN(num)) return "—";
  if (Math.abs(num) >= 1_000_000_000) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 }).format(num);
  }
  if (Math.abs(num) >= 1_000_000) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 }).format(num);
  }
  if (Math.abs(num) >= 1_000) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 }).format(num);
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Year options for filter: current year down to 2000
const YEAR_OPTIONS = Array.from({ length: new Date().getFullYear() + 1 - 2000 }, (_, i) => 2000 + i).reverse();

// Format billing period as "Month YYYY" (e.g. November 2025) from date string
const formatMonthYear = (dateString) => {
  if (!dateString) return "—";
  try {
    const s = String(dateString);
    const match = s.match(/^(\d{4})-(\d{2})/);
    if (match) {
      const [, y, m] = match;
      const monthNum = parseInt(m, 10);
      return `${MONTH_NAMES[monthNum - 1]} ${y}`;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
  } catch {
    return "—";
  }
};

const CustomerInvoices = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { customerOrgCode } = useParams();

  const customerName = location.state?.customerName || "Customer";

  const isMSPUser = isMSP();
  const {
    invoices,
    isFetchingInvoices,
    fetchInvoicesError,
    invoicesPagination,
    customerMetrics,
    isFetchingCustomerMetrics,
    customerMetricsError,
    invoiceRuleSnapshots,
    isFetchingInvoiceRuleSnapshots,
    fetchInvoiceRuleSnapshotsError,
  } = useSelector((state) => state.billingManagement);

  const [yearFilter, setYearFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [downloadSnackbar, setDownloadSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [rulesModalOpen, setRulesModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  // Download invoice: get pre-signed URL for this customer's invoice, same as index.js
  const handleDownloadInvoice = async (invoiceId) => {
    if (!customerOrgCode || !invoiceId) return;
    setDownloadingInvoiceId(invoiceId);
    setDownloadSnackbar((prev) => ({ ...prev, open: false }));
    try {
      const response = await getInvoiceDownload(customerOrgCode, invoiceId);
      if (response?.status === 200 && response?.url) {
        window.open(response.url, "_blank", "noopener,noreferrer");
        setDownloadSnackbar({ open: true, message: "Download started.", severity: "success" });
      } else {
        const data = response?.data ?? response;
        const msg = data?.detail ?? data?.error ?? data?.message ?? response?.message ?? "Download not available for this invoice.";
        setDownloadSnackbar({ open: true, message: String(msg), severity: "error" });
      }
    } catch (error) {
      const data = error?.response?.data ?? error?.data;
      const message = data?.detail ?? data?.error ?? data?.message ?? error?.message ?? "Download failed.";
      setDownloadSnackbar({ open: true, message: String(message), severity: "error" });
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const handleOpenRulesModal = (invoiceId) => {
    if (!customerOrgCode || !invoiceId) return;
    setSelectedInvoiceId(invoiceId);
    setRulesModalOpen(true);
    dispatch(getInvoiceRuleSnapshots({ customerOrgCode, invoiceId }));
  };

  const handleCloseRulesModal = () => {
    setRulesModalOpen(false);
    setSelectedInvoiceId(null);
  };

  useEffect(() => {
    if (!isMSPUser) {
      history.push("/dashboard");
      return;
    }
  }, [isMSPUser, history]);

  useEffect(() => {
    if (customerOrgCode && isMSPUser) {
      const params = { page, page_size: pageSize };
      if (yearFilter) params.year = yearFilter;
      dispatch(getCustomerInvoices({ customerOrgCode, ...params }));
    }
  }, [customerOrgCode, isMSPUser, dispatch, page, pageSize, yearFilter]);

  // Fetch customer-scope billing metrics (API: scope=customer, customer_orgcode required)
  useEffect(() => {
    if (customerOrgCode && isMSPUser) {
      const payload = { customerOrgCode };
      if (yearFilter) payload.year = yearFilter;
      dispatch(getCustomerBillingMetrics(payload));
    }
  }, [customerOrgCode, isMSPUser, dispatch, yearFilter]);

  const filteredInvoices = (invoices || []).filter((inv) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (inv.invoice_id && inv.invoice_id.toLowerCase().includes(term)) ||
      (inv.billing_period_start && inv.billing_period_start.includes(term)) ||
      (inv.billing_period_end && inv.billing_period_end.includes(term))
    );
  });

  // Metric cards use API customer-scope metrics when available; fallback to list-derived values only for table context
  const totalInvoices = customerMetrics?.total_invoices ?? invoicesPagination?.total ?? 0;
  const totalBilled = customerMetrics?.total_billed ?? (invoices || []).reduce((sum, inv) => sum + (getInvoiceAmount(inv) || 0), 0);
  const latestInvoiceAmount = customerMetrics?.latest_invoice_amount ?? (invoices?.[0] ? getInvoiceAmount(invoices[0]) : null);

  if (!customerOrgCode) {
    history.replace("/billing-management");
    return null;
  }

  return (
    <Box
      sx={{
        padding: { xs: "1rem", sm: "1.5rem", md: "2rem" },
        mt: "4rem",
        mb: "1.5rem",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
        fontFamily: '"Inter", system-ui, sans-serif',
      }}
    >
      <CustomerInvoicesHeader customerName={customerName} />

      <CustomerInvoicesMetrics
        customerName={customerName}
        totalInvoices={totalInvoices}
        totalBilled={totalBilled}
        latestInvoiceAmount={latestInvoiceAmount}
        lastInvoicePeriodStart={invoices?.[0]?.billing_period_start}
        customerMetrics={customerMetrics}
        isFetchingCustomerMetrics={isFetchingCustomerMetrics}
        customerMetricsError={customerMetricsError}
      />

      {/* Table card: filters in header + table */}
      <Card sx={{ borderRadius: "8px", border: "1px solid #e5e7eb", boxShadow: "none", backgroundColor: "#ffffff", overflow: "hidden" }}>
        {/* Table header: Year dropdown + Search */}
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, p: 2, borderBottom: "1px solid #e5e7eb", backgroundColor: "#fafafa" }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={yearFilter || ""}
              onChange={(e) => setYearFilter(e.target.value ? Number(e.target.value) : "")}
              displayEmpty
              renderValue={(v) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <CalendarMonthIcon sx={{ color: "#64748b", fontSize: 20 }} />
                  <span>{v ? `Year: ${v}` : "All years"}</span>
                </Box>
              )}
              sx={{
                height: 40,
                backgroundColor: "#ffffff",
                borderRadius: 1,
                "& .MuiSelect-select": { py: 1.25, pl: 1.5 },
              }}
              MenuProps={{
                PaperProps: { sx: { maxHeight: 320, mt: 1.5, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" } },
              }}
            >
              <MenuItem value="">
                <em>All years</em>
              </MenuItem>
              {YEAR_OPTIONS.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Search by month or invoice ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 260, "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
          />
        </Box>
        <TableContainer>
          <Table size="medium" stickyHeader sx={{ minWidth: 720, fontSize: "0.875rem" }}>
            <TableHead>
              <TableRow sx={{ "& th": { backgroundColor: "#f4f4f5", borderBottom: "2px solid #e4e4e7", py: 1.5, px: 2, fontWeight: 600, fontSize: "0.875rem", color: "#52525b" } }}>
                <TableCell>Billing period</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Run Type</TableCell>
                <TableCell>Requested at</TableCell>
                <TableCell>Finalized at</TableCell>
                <TableCell>Applied Rules</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetchingInvoices ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} sx={{ color: "#3b82f6" }} />
                  </TableCell>
                </TableRow>
              ) : fetchInvoicesError ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: "#ef4444", fontSize: "0.875rem" }}>
                    {typeof fetchInvoicesError === "string"
                      ? fetchInvoicesError
                      : (fetchInvoicesError?.detail || fetchInvoicesError?.error || fetchInvoicesError?.message || "Failed to load invoices.")}
                  </TableCell>
                </TableRow>
              ) : filteredInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, color: "#71717a", fontSize: "0.875rem" }}>
                    No invoices found
                  </TableCell>
                </TableRow>
              ) : (
                filteredInvoices.map((inv) => {
                  const monthLabel = formatMonthYear(inv.billing_period_start || inv.billing_period_end);
                  return (
                    <TableRow
                      key={inv.invoice_id}
                      sx={{
                        "& td": { borderBottom: "1px solid rgba(0,0,0,0.06)", py: 1.5, px: 2, fontSize: "0.875rem" },
                        "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                      }}
                    >
                      <TableCell sx={{ color: "#52525b" }}>{monthLabel}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography sx={{ fontWeight: 600, color: "#0f172a", fontSize: "0.875rem" }}>
                            {formatCurrency(inv.total_calculated_amount)}
                          </Typography>
                          {getShortInvoiceRef(inv.invoice_id) && (
                            <Typography sx={{ display: "block", fontSize: "0.75rem", color: "#94a3b8", fontFamily: "monospace", mt: 0.25 }}>
                              {getShortInvoiceRef(inv.invoice_id)}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={inv.status || "—"}
                          size="small"
                          sx={{
                            backgroundColor: inv.status === "FINALIZED" ? alpha("#10b981", 0.1) : alpha("#3b82f6", 0.1),
                            color: inv.status === "FINALIZED" ? "#10b981" : "#3b82f6",
                            fontWeight: 500,
                            fontSize: "0.75rem",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const runType = (inv.generation_type || "").toUpperCase();
                          if (runType === "ON_DEMAND") {
                            return (
                              <Tooltip title="On demand">
                                <TouchAppIcon sx={{ color: "#3b82f6", fontSize: 22 }} />
                              </Tooltip>
                            );
                          }
                          if (runType === "RECURRING" || runType === "SCHEDULED") {
                            return (
                              <Tooltip title={runType === "SCHEDULED" ? "Scheduled" : "Recurring"}>
                                <ScheduleIcon sx={{ color: "#3b82f6", fontSize: 22 }} />
                              </Tooltip>
                            );
                          }
                          return <Typography sx={{ color: "#52525b", fontSize: "0.875rem" }}>{inv.generation_type || "—"}</Typography>;
                        })()}
                      </TableCell>
                      <TableCell sx={{ color: "#71717a" }}>{formatDateTime(inv.requested_at)}</TableCell>
                      <TableCell sx={{ color: "#71717a" }}>{formatDateTime(inv.finalized_at)}</TableCell>
                      <TableCell>
                        <Tooltip title="View rule snapshots used for this invoice">
                          <Chip
                            icon={<ReceiptLongIcon sx={{ fontSize: 16 }} />}
                            label={inv.rule_application_count != null ? inv.rule_application_count : "0"}
                            size="small"
                            onClick={() => handleOpenRulesModal(inv.invoice_id)}
                            sx={{
                              cursor: "pointer",
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              borderRadius: "6px",
                              backgroundColor: "#f1f5f9",
                              color: "#475569",
                              "&:hover": { backgroundColor: "#e2e8f0", color: "#334155" },
                              "& .MuiChip-icon": { color: "inherit" },
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Download invoice">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!inv.invoice_id || downloadingInvoiceId === inv.invoice_id}
                              onClick={() => handleDownloadInvoice(inv.invoice_id)}
                              sx={{ color: "#71717a", "&:hover:not(.Mui-disabled)": { color: "#18181b", bgcolor: "rgba(0,0,0,0.04)" } }}
                            >
                              {downloadingInvoiceId === inv.invoice_id ? (
                                <CircularProgress size={20} sx={{ color: "#71717a" }} />
                              ) : (
                                <DownloadIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog
        open={rulesModalOpen}
        onClose={handleCloseRulesModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" } }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1rem", borderBottom: "1px solid #e4e4e7", pb: 1.5 }}>
          Invoice Rule Snapshots
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 2.5, px: 2.5, minHeight: 120 }}>
          {isFetchingInvoiceRuleSnapshots ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
              <CircularProgress size={32} sx={{ color: "#3b82f6" }} />
            </Box>
          ) : fetchInvoiceRuleSnapshotsError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {typeof fetchInvoiceRuleSnapshotsError === "string"
                ? fetchInvoiceRuleSnapshotsError
                : fetchInvoiceRuleSnapshotsError?.detail || fetchInvoiceRuleSnapshotsError?.message || "Failed to load rule snapshots."}
            </Alert>
          ) : invoiceRuleSnapshots?.items?.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1.5 }}>
              {invoiceRuleSnapshots.items.map((item, idx) => {
                const snap = item.rule_snapshot || {};
                const ruleType = snap.type || snap.rule_type || "";
                const params = snap.parameters && typeof snap.parameters === "object" ? snap.parameters : {};
                const scopes = Array.isArray(snap.scopes) ? snap.scopes : [];
                const hasScopes = scopes.length > 0;
                const scopeByType = scopes.reduce((acc, s) => {
                  const t = s.scopeType || s.scope_type || "OTHER";
                  if (!acc[t]) acc[t] = [];
                  acc[t].push(s);
                  return acc;
                }, {});
                const scopeTypeLabel = (t) => {
                  if (t === "SERVICE") return "AWS Services";
                  if (t === "REGION") return "AWS Regions";
                  if (t === "LINKED_ACCOUNT") return "Linked Accounts";
                  return t.replace(/_/g, " ");
                };
                return (
                  <Accordion
                    key={item.billing_rule_id || idx}
                    disableGutters
                    square
                    sx={{
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      "&:before": { display: "none" },
                      "&.Mui-expanded": { mt: 0, mb: 0 },
                      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: "#64748b" }} />}
                      sx={{
                        backgroundColor: "#ffffff",
                        borderRadius: "8px",
                        minHeight: 56,
                        "& .MuiAccordionSummary-content": { my: 1.5, alignItems: "center" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "#0f172a", lineHeight: 1.3 }}>
                            {snap.name || "—"}
                          </Typography>
                          {item.applied_at && (
                            <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", fontWeight: 400 }}>
                              Applied {formatDateTime(item.applied_at)}
                            </Typography>
                          )}
                        </Box>
                        {item.total_impact_amount != null && (
                          <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "#0f172a", flexShrink: 0 }}>
                            {formatCurrency(item.total_impact_amount)}
                          </Typography>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0 }}>
                      {/* Configuration Parameters */}
                      {Object.keys(params).length > 0 && (
                        <Box sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: "#ffffff" }}>
                          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1.5, textTransform: "uppercase" }}>
                            Configuration Parameters
                          </Typography>
                          <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 1.5, lineHeight: 1.5 }}>
                            Parameters used when this rule was applied at invoice generation time.
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {Object.entries(params).map(([key, value]) => {
                              const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
                              return (
                                <Box key={key} sx={{ p: 1.5, backgroundColor: "#f8fafc", borderRadius: "6px", border: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 600 }}>{label}</Typography>
                                  <Typography sx={{ fontSize: "0.875rem", color: "#3b82f6", fontWeight: 600 }}>
                                    {typeof value === "boolean" ? (value ? "Enabled" : "Disabled") : String(value)}
                                    {key === "percent" && "%"}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                      {/* Application Frequency & Active Period */}
                      <Box sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: "#ffffff" }}>
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1.5, textTransform: "uppercase" }}>
                          Application Frequency
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                          <Typography sx={{ fontSize: "0.8125rem", color: "#64748b" }}>
                            {snap.frequency === "RECURRING"
                              ? "This rule was applied as recurring (each billing cycle)."
                              : "This rule was applied as one-time."}
                          </Typography>
                          {snap.frequency && (
                            <Chip
                              label={snap.frequency === "RECURRING" ? "Recurring" : "One-time"}
                              size="small"
                              sx={{
                                backgroundColor: snap.frequency === "RECURRING" ? alpha("#3b82f6", 0.1) : alpha("#10b981", 0.1),
                                color: snap.frequency === "RECURRING" ? "#3b82f6" : "#10b981",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                              }}
                            />
                          )}
                        </Box>
                        {(snap.startDate || snap.start_date) && (snap.endDate || snap.end_date) && (
                          <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: "#f8fafc", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 0.5 }}>Active Period</Typography>
                            <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 500 }}>
                              {formatDate(snap.startDate || snap.start_date)} → {formatDate(snap.endDate || snap.end_date)}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Scope Filters */}
                      <Box sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: hasScopes ? "#ffffff" : "#f8fafc" }}>
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1.5, textTransform: "uppercase" }}>
                          Scope Filters
                        </Typography>
                        {hasScopes ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {Object.entries(scopeByType).map(([scopeType, items]) => (
                              <Box key={scopeType} sx={{ p: 1.5, backgroundColor: "#f8fafc", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                                <Typography sx={{ fontSize: "0.8125rem", color: "#1e293b", fontWeight: 600, mb: 0.75 }}>
                                  {scopeTypeLabel(scopeType)} — {items.some((i) => (i.operator || "").toUpperCase() === "EXCLUDE") ? "Exclude / Include" : "Include"}
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {items.map((s, i) => (
                                    <Chip
                                      key={i}
                                      label={`${(s.operator || "INCLUDE").toLowerCase() === "exclude" ? "Exclude" : "Include"}: ${s.scopeValue || s.scope_value || "—"}`}
                                      size="small"
                                      sx={{ fontSize: "0.75rem", backgroundColor: "#ffffff", color: "#475569", border: "1px solid #e2e8f0", fontWeight: 500 }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>
                            No scope filters. This rule applied to all accounts, services, and regions.
                          </Typography>
                        )}
                      </Box>

                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          ) : (
            <Typography sx={{ py: 3, color: "#71717a", fontSize: "0.875rem", textAlign: "center" }}>
              No rule snapshots for this invoice.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={downloadSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setDownloadSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert onClose={() => setDownloadSnackbar((prev) => ({ ...prev, open: false }))} severity={downloadSnackbar.severity}>
          {downloadSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerInvoices;
